import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import ErrorMessage from '@components-clean/core/error';
import Grid from '@components-clean/core/grid-fe';
import { DateCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import Loading from '@components-clean/core/loading/loading';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { NullableStudy } from '@contexts/local-state-context';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { StudyMemberFields, SmStudyMemberFields } from '@queries/generated/fragments.generated';
import {
    getRestrictedStudy,
    getRestrictedStudy_OpraQueries_study_Study,
    getRestrictedStudyVariables,
} from '@queries/generated/studies.generated';
import { getOrganisation, getOrganisationVariables } from '@queries/generated/uam.generated';
import { GET_RESTRICTED_STUDY } from '@queries/studies';
import { GET_ORGANISATION } from '@queries/uam';
import Modules, { IModule } from '@config/modules';

interface StudyMemberExtendedFields extends StudyMemberFields {
    isOpraUser?: boolean;
}

interface SmStudyMemberExtendedFields extends SmStudyMemberFields {
    isOpraUser?: boolean;
}

interface Props {
    study: NullableStudy;
    module: IModule;
    isSmStudyTeam?: boolean;
}

const StudyTeam = ({ study, module, isSmStudyTeam }: Props) => {
    const { t } = useTranslation();

    const [users, setUsers] = useState<StudyMemberExtendedFields[] | SmStudyMemberExtendedFields[]>([]);
    const [loadedStudy, setLoadedStudy] = useState<getRestrictedStudy_OpraQueries_study_Study | null>(null);

    const [getItems, { loading, error, data: dataOrg }] = useLazyQuery<getOrganisation, getOrganisationVariables>(
        GET_ORGANISATION,
    );
    const [loadStudyData, { loading: loadingStudy, data: studyData }] = useLazyQuery<
        getRestrictedStudy,
        getRestrictedStudyVariables
    >(GET_RESTRICTED_STUDY);

    useEffect(() => {
        let array: StudyMemberExtendedFields[] | SmStudyMemberExtendedFields[] = [];
        if (loadedStudy && dataOrg?.organisation) {
            const membersKey = isSmStudyTeam ? 'smMembers' : 'members';
            loadedStudy[membersKey]?.forEach((item) => {
                if (item) {
                    const newItem = {
                        ...item,
                        isOpraUser: !!item.user,
                    };
                    // @ts-ignore
                    array.push(newItem);
                }
            });
        }

        array = restructuredUpdatedCreatedBy(array) as StudyMemberExtendedFields[];
        setUsers(
            orderBy(array, [
                { field: 'active', dir: 'desc' },
                { field: 'isOpraUser', dir: 'desc' },
                { field: 'lastName', dir: 'asc' },
            ]),
        );
    }, [study, loadedStudy, dataOrg]);

    useEffect(() => {
        if (study) loadStudyData({ variables: { id: study.id as string } }).catch((err) => window.console.error(err));
    }, [study]);

    useEffect(() => {
        if (studyData?.study) {
            setLoadedStudy(studyData?.study);
            getItems().catch((err) => window.console.error(err));
        } else {
            setLoadedStudy(null);
        }
    }, [studyData]);

    const exportFilename = useMemo(() => {
        const baseFilename = `Study Team_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
            'DD-MMM-YYYY',
        )}`;

        return baseFilename;
    }, [study?.studyNumber, module.id]);

    const moduleMap = () => {
        switch (module) {
            case Modules.SubjectMonitoring:
                return AuditApplicationArea.SUBJECT_MONITORING;
            case Modules.CentralMonitoring2:
                return AuditApplicationArea.CENTRAL_MONITORING_2;
            default:
                return AuditApplicationArea.RISK_ASSESSMENT;
        }
    };

    if (loading || loadingStudy) return <Loading />;

    if (error) return <ErrorMessage />;

    return (
        <Grid
            title={t('common:studyTeam')}
            maxHeight="auto"
            testDataId="study-team-grid"
            data={users}
            showPlaceholder={false}
            showAddIcon={false}
            columns={[
                {
                    title: t('active'),
                    id: 'active',
                    field: 'active',
                    cells: { data: YesNoCell },
                    lockedByDefault: true,
                    filter: 'boolean',
                },
                {
                    title: t('opraUser'),
                    id: 'opra-user',
                    field: 'isOpraUser',
                    cells: { data: YesNoCell },
                    lockedByDefault: true,
                    filter: 'boolean',
                },
                {
                    title: t('lastName'),
                    id: 'last-name',
                    field: 'lastName',
                    lockedByDefault: true,
                },
                {
                    title: t('firstName'),
                    id: 'first-name',
                    field: 'firstName',
                },
                {
                    title: t('email'),
                    id: 'email',
                    field: 'email',
                },
                {
                    title: t('uam:studyAdministrator'),
                    id: 'administrator',
                    field: 'isAdmin',
                    cells: { data: YesNoCell },
                    filter: 'boolean',
                },
                {
                    title: t('ra:createdDate'),
                    field: 'created',
                    id: 'study-member-created',
                    filter: 'date',
                    cells: { data: DateCell },
                    cellName: 'DateCell',
                },
                {
                    title: t('ra:createdBy'),
                    field: '_createdByString',
                    id: 'study-member-createdby',
                },
                {
                    title: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'study-member-updated',
                    filter: 'date',
                    cells: { data: DateCell },
                    cellName: 'DateCell',
                },
                {
                    title: t('ra:updatedBy'),
                    field: '_updatedByString',
                    id: 'study-member-updatedby',
                },
                {
                    title: 'ID',
                    field: 'recordId',
                    showByDefault: false,
                    filter: 'numeric',
                    id: 'study-team-id',
                },
            ]}
            exportFilename={exportFilename}
            service={AuditServiceType.CORE}
            area={moduleMap()}
            dataType={AuditDataType.EXCEL_EXPORT}
        />
    );
};

export default StudyTeam;
